import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '@/types';
import { authApi } from '@/lib/api';

interface AuthState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;

  login: (username: string, password: string) => Promise<void>;
  loginWithGoogle: (user: User, token: string) => void;
  logout: () => Promise<void>;
  checkAuth: () => Promise<boolean>;
  isViewer: () => boolean;
  isAdmin: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isLoading: false,
      isAuthenticated: false,

      login: async (username: string, password: string) => {
        set({ isLoading: true });
        try {
          const response = await authApi.login(username, password);
          localStorage.setItem('wbs_auth_token', response.token);
          localStorage.setItem('wbs_user', JSON.stringify(response.user));
          set({
            user: response.user,
            token: response.token,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      loginWithGoogle: (user: User, token: string) => {
        localStorage.setItem('wbs_auth_token', token);
        localStorage.setItem('wbs_user', JSON.stringify(user));
        set({
          user,
          token,
          isAuthenticated: true,
          isLoading: false,
        });
      },

      logout: async () => {
        try {
          await authApi.logout();
        } catch (error) {
          console.error('Logout error:', error);
        } finally {
          localStorage.removeItem('wbs_auth_token');
          localStorage.removeItem('wbs_user');
          set({
            user: null,
            token: null,
            isAuthenticated: false,
          });
        }
      },

      checkAuth: async () => {
        const token = localStorage.getItem('wbs_auth_token');

        if (!token) {
          set({ isAuthenticated: false, user: null, token: null });
          return false;
        }

        try {
          const result = await authApi.verify(token);
          if (!result.valid || !result.user) {
            localStorage.removeItem('wbs_auth_token');
            localStorage.removeItem('wbs_user');
            set({ isAuthenticated: false, user: null, token: null });
            return false;
          }
          // 서버에서 받은 최신 user 정보로 갱신
          const user = result.user;
          localStorage.setItem('wbs_user', JSON.stringify(user));
          set({ user, token, isAuthenticated: true });
          return true;
        } catch (error) {
          localStorage.removeItem('wbs_auth_token');
          localStorage.removeItem('wbs_user');
          set({ isAuthenticated: false, user: null, token: null });
          return false;
        }
      },

      isViewer: () => get().user?.role === 'viewer',
      isAdmin: () => get().user?.role === 'admin',
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
